import { useQuery } from '@apollo/client';
import {
  Container,
  Divider,
  FlexGrid,
  Header,
  Picture,
  Text,
  Visibility,
} from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import { Fragment, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useGeoIpInfo from '../../../lib/hooks/useGeoIpInfo';
import { ANALYTICS_EVENTS, COUNTRY_CODES, SUPPORTED_LANGUAGES } from '../../constants';
import * as TESTIMONIALS from '../../constants/testimonials';
import AnimatedPhone from '../AnimatedPhone';
import AppPromo from '../AppPromo';
import ClientOnly from '../ClientOnly'; // TODO: Replace with react-components v9.0.2 version
import HomePageGasBackBrands from '../FeaturedGasbackBrands/HomePageGasBackBrands';
import GetTheAppButton from '../GetTheAppButton';
import HomeScreenCardModal from '../HomeScreenCardModal';
import LPT1Hero from '../LandingPageTemplate1/Hero';
import { HeroSideInfo, Hero as LPT2Hero } from '../LandingPageTemplate2/Sections/Heroes';
import MobileDownloadAd from '../MobileDownloadAd';
import Page from '../Page';
import ReachMetrics from '../ReachMetrics';
import SavingsTools from '../SavingsTools';
import SearchStateCloud from '../SearchStateCloud';
import Testimonials from '../Testimonials';
import TestimonialCard from '../Testimonials/TestimonialCard';
import styles from './HomePage.module.css';

const cx = classnames.bind(styles);

function HeroPhoneImage({
  ...rest
}) {
  const imagePath = 'https://static.gasbuddy.com/web/consumer/phones/iphone_X_list_CA_020520';

  return (
    <Picture
      srcSet={`
        ${imagePath}@2x.png 2x,
        ${imagePath}@3x.png 3x
      `}
      src={`${imagePath}.png`}
      {...rest}
    >
      <Picture.Source
        srcSet={`
          ${imagePath}.webp,
          ${imagePath}@2x.webp 2x,
          ${imagePath}@3x.webp 3x
        `}
        type="image/webp"
      />
    </Picture>
  );
}

export const GET_PAY_PAGE = gql`
  query PayPage {
    offers {
      partner {
        imageUrl
        name
      }
      tags
    }
    marketingMetrics {
      savedByUser
      platformTotals {
        key
        value
      }
    }
    page(url: "pay") {
      ... on CMSLandingPage {
        product {
          offers {
            price {
              amount
            }
          }
        }
        sections {
          ctaLabel
          ctaUrl
          description
          image {
            url
          }
          imageAlt
          mobileImage {
            url
          }
          name
          title
        }
        sectionList {
          backgroundColor
          blocks {
            alt
            ctaDisclaimer
            ctaLabel
            ctaUrl
            customHtml
            description
            descriptionAlignment
            descriptionSize
            image {
              url
            }
            title
            titleAlignment
            titleImage {
              url
            }
            titleSize
          }
          ctaDisclaimer
          ctaLabel
          ctaUrl
          displayName
          name
          subtitle
          textColor
          title
          titleImage {
            url
          }
        }
      }
    }
  }
`;

export default function Homepage() {
  const { t, i18n } = useTranslation(['testimonials', 'marketing']);
  const { country } = useGeoIpInfo();
  const testimonials = TESTIMONIALS[country] || TESTIMONIALS.CA;
  const isUSUser = country === COUNTRY_CODES.USA;
  const { data } = useQuery(GET_PAY_PAGE, {
    skip: !isUSUser,
  });
  const offers = data?.offers.map(offer => ({
    title: offer.partner?.name,
    logo: offer.partner?.imageUrl ? `//images.gasbuddy.io/autox100/${offer.partner.imageUrl}` : undefined,
    tags: offer.tags,
  })) || [];
  const { platformTotals, savedByUser } = data?.marketingMetrics || {};
  // filter out offers that don't have a logo so that we don't just show an empty bubble
  const featuredOffers = offers?.filter(({ logo, tags }) => tags && tags.includes('featured_web') && !!logo);
  const offerPrice = data?.page?.product?.offers?.[0]?.price?.amount;
  const location = useLocation();
  const analytics = useAnalytics();

  testimonials.sort((a, b) => b.id - a.id);

  const handleEnrollClick = useCallback(() => {
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.PAY_CTA_CLICKED,
      attributes: {
        'Screen Name': 'Home',
        Section: 'PayPromo',
      },
    });
  }, [analytics]);

  let helmetTitle = t('meta.homePageUSTitle', { ns: 'marketing' });
  let helmetDescription = t('meta.homePageUSDescription', { ns: 'marketing' });

  if (!isUSUser) {
    helmetTitle = t('meta.homePageCATitle', { ns: 'marketing' });
    helmetDescription = t('meta.homePageCADescription', { ns: 'marketing' });
  }

  const heroCMSSection = useMemo(() => {
    if (!isUSUser) {
      return null;
    }

    const heroCMSSectionTemplate1 = data?.page?.sections?.find(section => section.name === 'hero');

    if (heroCMSSectionTemplate1) {
      return (
        <LPT1Hero
          buttonHref={`${heroCMSSectionTemplate1.ctaUrl}${location.search}`}
          heroData={heroCMSSectionTemplate1}
          offerPrice={offerPrice}
          onButtonClick={handleEnrollClick}
        />
      );
    }

    const heroCMSSectionTemplate2 = data?.page?.sectionList?.find(section => section.name.match(/^hero\w*$/i));
    if (heroCMSSectionTemplate2) {
      switch (heroCMSSectionTemplate2.name) {
        case 'hero':
          return <LPT2Hero {...heroCMSSectionTemplate2} />;
        case 'heroSideInfo':
          return (
            <HeroSideInfo
              {...heroCMSSectionTemplate2}
              onClick={handleEnrollClick}
            />
          );
        case 'heroSideInfoRight':
          return (
            <HeroSideInfo
              {...heroCMSSectionTemplate2}
              infoRight
              onClick={handleEnrollClick}
            />
          );
        default:
          return null;
      }
    }

    return null;
  }, [data, handleEnrollClick, isUSUser, location.search, offerPrice]);

  const hrefLang = i18n.language === SUPPORTED_LANGUAGES.ENGLISH
    ? `en-${country.toLowerCase()}`
    : i18n.language;
  const heroPhoneImageAlt = t('appPromo.iPhoneImageAlt', { ns: 'marketing' });

  return (
    <div className={cx('container')}>
      <Page name="Home" compact>
        <Helmet>
          <title>{helmetTitle}</title>
          <meta property="og:title" content={helmetTitle} />
          <meta name="twitter:title" content={helmetTitle} />
          <meta name="description" content={helmetDescription} />
          <meta property="og:description" content={helmetDescription} />
          <meta name="twitter:description" content={helmetDescription} />
          <link rel="alternate" href="https://www.gasbuddy.com" hrefLang={hrefLang} />
        </Helmet>
        <ClientOnly>
          <HomeScreenCardModal />
        </ClientOnly>
        <div className={cx('content')}>
          <Visibility desktop={false} tablet={false}>
            <Container>
              <MobileDownloadAd analyticsScreenName="Home_Page" />
              <br />
              <GetTheAppButton cta={false} screen="Home" section="Mobile Top App Promo" />
              <AnimatedPhone country={country} />
            </Container>
          </Visibility>
          {heroCMSSection || (
            <div className={cx('hero')}>
              <div className={cx('header')}>
                <Container>
                  <Header className={cx('restrict', 'mainHeader')} color="white" elongated uppercase>
                    {t('appPromo.homeHeader', { ns: 'marketing' })}
                  </Header>
                </Container>
              </div>
              <Container className={cx('imageContainer')}>
                <HeroPhoneImage
                  className={cx('phone')}
                  alt={heroPhoneImageAlt}
                />
              </Container>
              <Container>
                <FlexGrid>
                  <FlexGrid.Column desktop={10}>
                    <Header as="h2" className={cx('lead')}>
                      {t('appPromo.homeSubHeader', { ns: 'marketing' })}
                    </Header>
                    <Text as="p" className={cx('sublead')}>
                      {t('appPromo.homeSubHeaderInfo', { ns: 'marketing' })}
                    </Text>
                    <br />
                    <br />
                    <GetTheAppButton className={cx('mobileFluid')} screen="Home" section="Hero Promo" />
                  </FlexGrid.Column>
                </FlexGrid>
              </Container>
              <Visibility mobile tablet={false} desktop={false}>
                <HeroPhoneImage alt={heroPhoneImageAlt} />
              </Visibility>
            </div>
          )}
          {isUSUser && (
            <div className={cx('pwgbContainerWrapper')}>
              <HomePageGasBackBrands
                brands={featuredOffers}
                className={cx('gasBackOffers')}
              />
            </div>
          )}
          {!!(savedByUser && platformTotals) && (
            <Fragment>
              <Container className={cx('metricsContainer')}>
                <div className={cx('metricsOffset')}>
                  <ReachMetrics platformTotals={platformTotals} country={country} />
                </div>
              </Container>
              <Divider className={cx('homeDivider')} />
              <Container>
                <Testimonials className={cx('testimonials')}>
                  {testimonials.map(({ username, name, stars = 0 }) => (
                    <TestimonialCard
                      key={name}
                      text={t(`testimonialText_${name}`, { ns: 'testimonials' })}
                      from={name}
                      stars={stars}
                      saved={savedByUser[username]}
                    />
                  ))}
                </Testimonials>
              </Container>
            </Fragment>
          )}
          <AppPromo className={cx('homeAppPromo')} country={country} screenName="Home" />
          <SavingsTools className={cx('savings')} />
          <Container className={cx('regionsContainer')} fluid>
            <SearchStateCloud country={country} />
          </Container>
        </div>
      </Page>
    </div>
  );
}
